<template>
  <d-container
    fluid
    class="main-content-container px-4">
    <!-- Page Header -->
    <d-row
      no-gutters
      class="page-header py-4">
      <d-col
        col
        sm="6"
        class="text-center text-sm-left mb-4 mb-sm-0">
        <span class="text-uppercase page-subtitle">
          {{ $t('purchase.ordersFromSuppliers') }}
        </span>
        <h3 class="page-title">
          {{ $t('order.ordersList') }}
        </h3>
      </d-col>
      <d-col
        col
        sm="6"
        class="text-right text-sm-right d-flex justify-content-end align-items-end">
        <d-checkbox
          v-if="userIsWorker"
          inline
          v-model="filters.show_all"
          toggle
          toggle-small>
          {{ $t('order.showAllOrders') }}
        </d-checkbox>

        <d-button
          theme="success"
          @click.prevent="$router.push('/zakupy/nowe')">
          <i class="material-icons">add</i>
          {{ $t('purchase.addOrder') }}
        </d-button>
      </d-col>
    </d-row>

    <d-row
      style="width: 100%;"
      class="d-flex ml-0 mb-3 page-filters">
      <validation-observer
        ref="purchases-filters-form"
        tag="form"
        style="width: 100%;"
        class="d-flex">
        <d-input
          :placeholder="$t('purchase.enterOrderName')"
          v-model="filters.order_name"
          class="w-50" />

        <d-input
          :placeholder="$t('purchase.enterInvoiceNumber')"
          v-model="filters.search"
          class="w-50 ml-2" />

        <v-select
          class="ml-2 w-50"
          :options="customerOptions"
          v-model="filters.customer"
          :placeholder="$t('shippers.shipperQuestionMark')"
          :searchable="false"
          label="text">
        </v-select>

        <v-select
          class="ml-2 w-50"
          :options="purchaseStatusOptions"
          v-model="filters.status"
          :placeholder="$t('status.statusQuestionMark')"
          :searchable="false"
          label="text">
        </v-select>

        <v-select
          class="ml-2 w-50"
          :options="sewingRoomStatusOptions"
          v-model="filters.sewing_room_status"
          :placeholder="$t('status.statusInSewingRoomQuestionMark')"
          :searchable="false"
          label="text">
        </v-select>

        <d-button
          theme="info"
          outline
          class="text-nowrap ml-2"
          @click.prevent="validateFiltersForm">
          {{ $t('ui.search') }}
        </d-button>

        <d-button
          theme="danger"
          outline
          class="text-nowrap ml-2"
          @click.prevent="clearFilters">
          {{ $t('ui.resetFilters') }}
        </d-button>

        <d-button
          theme="success"
          class="ml-2 text-nowrap"
          @click.prevent.stop="loadFilteredData(true)">
          <div
            v-if="downloadInProgress"
            class="spinner-border spinner-border-sm"
            role="status"></div>
          <template v-if="!downloadInProgress">
            {{ $t('order.downloadXls') }}
          </template>
        </d-button>
      </validation-observer>
    </d-row>

    <d-card style="width: 100%;">
      <table class="table mt-0 mb-0 full-width">
        <thead class="bg-light">
          <tr>
            <th scope="col" class="border-0" style="width: 50px;">ID</th>
            <th scope="col" class="border-0" style="width: 200px;">{{ $t('status.status') }}</th>
            <th scope="col" class="border-0" style="width: 160px;">{{ $t('status.statusInSewingRoom') }}</th>
            <th scope="col" class="border-0">{{ $t('ui.supplier') }}</th>
            <th scope="col" class="border-0" style="width: 300px;">{{ $t('ui.orderName') }}</th>
            <th scope="col" class="border-0" style="width: 120px;">{{ $t('ui.deadlineCap') }}</th>
            <th scope="col" class="border-0" style="width: 160px;">{{ $t('ui.actions') }}</th>
          </tr>
        </thead>
        <tbody v-if="!loadError">
          <tr
            v-for="(purchase, index) of purchases"
            :key="'container-' + index">
            <td class="align-middle">
              {{ purchase.id }}
            </td>
            <td class="align-middle">
              <d-badge
                v-if="purchase.status === 'NEW'"
                theme="primary">
                {{ $t('purchase.newOrder') }}
              </d-badge>
              <d-badge
                v-if="purchase.status === 'WAITING_PAYMENT'"
                theme="warning">
                {{ $t('order.pendingPayment') }}
              </d-badge>
              <d-badge
                v-if="purchase.status === 'WAITING_PRODUCTS'"
                theme="warning">
                {{ $t('order.waitingPurchaseProducts') }}
              </d-badge>
              <d-badge
                v-if="purchase.status === 'CLOSED'"
                theme="danger">
                {{ $t('purchase.closed') }}
              </d-badge>
              <d-badge
                v-if="!purchase.status"
                theme="light">
                {{ $t('ui.lack') }}
              </d-badge>

              <d-badge
                v-if="purchase.products_received"
                theme="success"
                :outline="true"
                class="mt-1">
                {{ $t('purchase.allProductsReceived') }}
              </d-badge>

              <small
                v-if="purchase.exclude_from_erp_integration"
                class="text-danger">
                {{ $t('order.erp.isExcluded') }}
              </small>

              <d-badge
                v-if="!purchase.exclude_from_erp_integration"
                :theme="purchase.erp_optima_updated_at ? 'success' : 'danger'"
                class="w-100 mt-1"
                :outline="true">
                <template v-if="purchase.erp_optima_updated_at">
                  {{ $t('order.erp.integratedWithErp') }}
                </template>
                <template v-else>
                  {{ $t('order.erp.notIntegratedWithErp') }}
                </template>
              </d-badge>
            </td>
            <td class="align-middle">
              <div v-if="purchase.sewing_room_status">
                <small>
                  {{ $t('ui.supplier') }}:
                </small><br>
                <d-badge
                  :theme="sewingRoomStatus[purchase.sewing_room_status].style"
                  :outline="sewingRoomStatus[purchase.sewing_room_status].outline">
                  {{ sewingRoomStatus[purchase.sewing_room_status].text[prefferedLanguage] }}
                </d-badge>
              </div>

              <div v-if="purchase.sub_sewing_room_status">
                <small>
                  {{ $t('ui.subSupplier') }}:
                </small><br>
                <d-badge
                  :theme="sewingRoomStatus[purchase.sub_sewing_room_status].style"
                  :outline="sewingRoomStatus[purchase.sub_sewing_room_status].outline">
                  {{ sewingRoomStatus[purchase.sub_sewing_room_status].text[prefferedLanguage] }}
                </d-badge>
              </div>
            </td>
            <td class="align-middle">
              {{ purchase.customer_name }}
              <template v-if="purchase.sub_customer_name">
                <br>
                <small>
                  {{ $t('ui.subSupplier') }}:
                  <strong>{{ purchase.sub_customer_name }}</strong>
                </small>
              </template>

              <br v-if="purchase.received_at || purchase.suborder_received_at">

              <small v-if="purchase.received_at">
                {{ $t('purchase.receivedAt') }}: <strong class="text-dark">{{ purchase.received_at }}</strong>
              </small>

              <br v-if="purchase.received_at && purchase.suborder_received_at">

              <small v-if="purchase.suborder_received_at">
                {{ $t('purchase.suborderReceivedAt') }}: <strong class="text-dark">{{ purchase.suborder_received_at }}</strong>
              </small>
            </td>
            <td class="align-middle">
              <template v-if="purchase.orders_names">{{ purchase.orders_names }}</template>
              <template v-else>&mdash;</template>
              <br>
              <small>
                <strong>{{ $t('purchase.erpNumber') }}: </strong>
                <template v-if="purchase.erp_number">{{ purchase.erp_number }}</template>
                <template v-else>&mdash;</template>
              </small>
            </td>
            <td class="align-middle">
              {{ purchase.date }}
            </td>
            <td class="align-middle" style="width: 150px;">
              <d-button
                theme="primary"
                @click.native="$router.push('/zakupy/edycja/' + purchase.id)"
                class="mr-2"
                :title="$t('ui.edit')">
                <i class="material-icons">edit</i>
              </d-button>
              <d-button
                v-if="purchase.status !== 'CLOSED'"
                theme="danger"
                :disabled="removeInProgress"
                :title="$t('ui.delete')"
                @click.native="removePurchase(purchase.id, purchase.can_delete)">
                <i class="material-icons">delete</i>
              </d-button>
            </td>
          </tr>
          <tr v-if="purchases.length === 0">
            <td
              colspan="9"
              class="text-center">
              {{ $t('purchase.noOrdersToDisplay') }}&hellip;
            </td>
          </tr>
        </tbody>
      </table>

      <div
        v-if="!allDataLoaded"
        class="pb-3 pt-3 text-center overlay-loading">
        {{ $t('ui.dataLoading') }}&hellip;
      </div>

      <d-alert
        v-if="loadError"
        show
        theme="warning">
        {{ $t('ui.dataLoadingFailed') }} <a href="javascript:window.location.reload();" class="alert-link">{{ $t('ui.refreshPage') }}</a> {{ $t('ui.toTryAgain') }}
      </d-alert>
    </d-card>

    <pagination
      :meta-data="paginationMetaData"
      :initial-page="currentPage" />
  </d-container>
</template>

<script>
import debounce from 'lodash.debounce';
import HasFilters from './../mixins/HasFilters.vue';
import HasPagination from './../mixins/HasPagination.vue';
import ListUtils from './../utils/ListUtils.js';
import FormUtils from './../utils/FormUtils.js';
import Pagination from './../components/common/Pagination.vue';
import getPrefferedLanguage from './../utils/i18nUtils.js';
import sewingRoomStatuses from './../data/sewing-room-status.js';
import Vue from 'vue';

export default {
  name: 'purchases',
  mixins: [
    HasFilters,
    HasPagination
  ],
  components: {
    'pagination': Pagination
  },
  computed: {
    allDataLoaded () {
      return this.loaded && this.loadedCustomers;
    },
    customerOptions () {
      return this.customers.map(customer => ({
        text: customer.name,
        value: customer.id
      }));
    },
    purchaseStatusOptions () {
      return [
        {
          text: this.$t('purchase.newOrder'),
          value: 'NEW'
        },
        {
          text: this.$t('order.waitingPurchaseProducts'),
          value: 'WAITING_PRODUCTS'
        },
        {
          text: this.$t('order.pendingPayment'),
          value: 'WAITING_PAYMENT'
        },
        {
          text: this.$t('purchase.closed'),
          value: 'CLOSED'
        }
      ];
    },
    sewingRoomStatusOptions () {
      let statuses = Object.keys(sewingRoomStatuses);
      let options = [];

      for (let i = 0; i < statuses.length; i++) {
        let status = statuses[i];
        options.push({
          text: sewingRoomStatuses[status].text[this.prefferedLanguage],
          value: status
        })
      }

      return options;
    },
    prefferedLanguage () {
      return getPrefferedLanguage();
    },
    sewingRoomStatus () {
      return sewingRoomStatuses;
    },
    userIsWorker () {
      return this.$store.getters.getUserType === 'worker';
    }
  },
  watch: {
    'filters.show_all': {
      handler () {
        if (this.allDataLoaded) {
          this.debouncedLoadFilteredData();
        }
      },
      deep: true
    }
  },
  data() {
    return {
      purchases: [],
      customers: [],
      loaded: false,
      loadError: false,
      loadedCustomers: false,
      removeInProgress: false,
      downloadInProgress: false,
      filters: {
        order_name: '',
        search: '',
        status: null,
        sewing_room_status: null,
        customer: null,
        show_all: false
      }
    };
  },
  mounted () {
    this.loadAdditionalData();
    this.loadFilteredData();
  },
  methods: {
    loadAdditionalData () {
      FormUtils.loadAdditionalData(this, {
        method: 'get',
        endpoint: '/customers/items?where[is_supplier]=1',
        outputKey: 'customers',
        errorKey: 'loadCustomersError',
        loadedKey: 'loadedCustomers'
      });
    },
    debouncedLoadFilteredData: debounce(function () {
      this.$bus.$emit('view-filters-save');
      this.loadFilteredData();
    }, 250),
    loadFilteredData (downloadAsFile = false) {
      let where = {};

      if (this.filters.search) {
        where.search = this.filters.search;
      }

      if (this.filters.status) {
        where.status = this.filters.status.value;
      }

      if (this.filters.customer) {
        where.customer_id = this.filters.customer.value;
      }

      if (this.filters.order_name) {
        where.order_name = this.filters.order_name;
      }

      if (this.filters.sewing_room_status && this.filters.sewing_room_status.value) {
        where.sewing_room_status = this.filters.sewing_room_status.value;
      }

      if (downloadAsFile) {
        if (this.downloadInProgress) {
          return;
        }

        where['download_excel'] = 1;
        this.downloadInProgress = true;

        let requestConfig = {
          where
        };

        if (this.userIsWorker && this.filters.show_all) {
          requestConfig.show_all = 1;
        }

        this.$http.post('/purchases/items', requestConfig, {
          responseType: 'blob'
        }).then((response) => {
          let link = document.createElement('a');
          link.href = URL.createObjectURL(new Blob([response.data]));
          link.setAttribute('download', this.$t('purchase.purchasesXlsFilename') + '.xlsx');
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          this.downloadInProgress = false;
        }).catch(() => {
          this.downloadInProgress = false;
        });
      } else {
        let requestConfig = {
          where
        };

        if (this.userIsWorker && this.filters.show_all) {
          requestConfig.show_all = 1;
        }

        ListUtils.loadPaginatedItemsData(this, {
          endpoint: '/purchases/items?page=' + this.currentPage,
          listField: 'purchases',
          params: requestConfig
        });
      }
    },
    clearFilters () {
      this.loaded = false;
      this.loadError = false;
      this.filters = {
        order_name: '',
        search: '',
        status: null,
        sewing_room_status: null,
        customer: null,
        show_all: false
      };

      this.$bus.$emit('view-filters-reset');
      this.$bus.$emit('pagination-reset', false);
    },
    validateFiltersForm () {
      this.$bus.$emit('pagination-reset');
      FormUtils.validate(this.$refs['purchases-filters-form'], this.debouncedLoadFilteredData);
    },
    removePurchase (purchaseID, canDelete) {
      if (!canDelete) {
        Vue.swal({
          title: this.$t('order.exisitngProductsInHplOrder'),
          html: this.$t('container.removeProductText'),
          type: 'warning',
          confirmButtonText: this.$t('ui.ok'),
          buttonsStyling: true
        });

        return;
      }

      this.removeInProgress = true;

      ListUtils.removeItem({
        $http: this.$http,
        endpointBase: '/purchases/delete/',
        id: purchaseID,
        successTitle: this.$t('order.orderDeleteSuccessTitle'),
        successText: this.$t('order.orderDeleteSuccessText'),
        confirmTitle: this.$t('order.orderDeleteConfirmTitle'),
        confirmText: this.$t('ui.deleteConfirmText'),
        errorTitle: this.$t('ui.errorOccured'),
        errorText: this.$t('order.orderDeleteErrorText'),
        successAction: () => {
          this.removeInProgress = false;
          this.loadFilteredData();
        },
        cancelAction: () => {
          this.removeInProgress = false;
        }
      });
    }
  }
};
</script>
